<script>
import Button from 'primevue/button';

export default {
    name: 'PartnerAdd',

    components: { Button },

    emits: ['added', 'open-links', 'cancel'],

    data() {
        return {
            name: '',
            code: '',
            codeAutoGenerated: true,
            source_type: '',
            description: '',
            error: null,
            newPartnerId: null,
            showLinkNudge: false,
            codeTooltipVisible: false,
        };
    },

    computed: {},

    watch: {
        name(val) {
            if (this.codeAutoGenerated) {
                this.code = this.slugify(val);
            }
        },
    },

    mounted() {},

    methods: {
        transliterate(text) {
            const map = {
                а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'yo',ж:'zh',з:'z',и:'i',
                й:'y',к:'k',л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',
                у:'u',ф:'f',х:'kh',ц:'ts',ч:'ch',ш:'sh',щ:'shch',ъ:'',ы:'y',
                ь:'',э:'e',ю:'yu',я:'ya',
                А:'A',Б:'B',В:'V',Г:'G',Д:'D',Е:'E',Ё:'Yo',Ж:'Zh',З:'Z',И:'I',
                Й:'Y',К:'K',Л:'L',М:'M',Н:'N',О:'O',П:'P',Р:'R',С:'S',Т:'T',
                У:'U',Ф:'F',Х:'Kh',Ц:'Ts',Ч:'Ch',Ш:'Sh',Щ:'Shch',Ъ:'',Ы:'Y',
                Ь:'',Э:'E',Ю:'Yu',Я:'Ya',
            };
            return text.split('').map((c) => (map[c] !== undefined ? map[c] : c)).join('');
        },

        slugify(name) {
            return this.transliterate(name)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },

        enableCodeEdit() {
            this.codeAutoGenerated = false;
            this.$nextTick(() => {
                this.$refs.codeInput?.focus();
            });
        },

        async submit() {
            this.error = null;
            if (!this.name.trim() || !this.code.trim()) {
                this.error = 'Name and code are required.';
                return;
            }
            try {
                const partner = await this.$store.dispatch('addPartner', {
                    name: this.name.trim(),
                    code: this.code.trim(),
                    source_type: this.source_type || null,
                    description: this.description.trim() || null,
                });
                this.newPartnerId = partner?.id ?? null;
                this.showLinkNudge = true;
                this.name = '';
                this.code = '';
                this.codeAutoGenerated = true;
                this.source_type = '';
                this.description = '';
                this.$emit('added', partner);
            } catch (e) {
                this.error = e?.response?.data?.detail ?? 'Failed to create partner.';
            }
        },

        dismissNudge() {
            this.showLinkNudge = false;
        },

        openLinkForNew() {
            this.showLinkNudge = false;
            if (this.newPartnerId) {
                this.$emit('open-links', this.newPartnerId);
            }
        },
    },
};
</script>

<template>
    <div class="partner-add-form">
        <div class="form-title">Add New Partner</div>

        <!-- Nudge -->
        <div v-if="showLinkNudge" class="link-nudge">
            <span>Partner created! Want to generate the first tracking link?</span>
            <Button label="Create Link" severity="success" size="small" @click="openLinkForNew" />
            <Button icon="pi pi-times" severity="secondary" text rounded size="small" @click="dismissNudge" />
        </div>

        <p v-if="error" class="error-text">{{ error }}</p>

        <!-- Row 1: Name + Code on one line -->
        <div class="form-row form-row--inline">
            <label class="form-label form-label--inline">Name *</label>
            <input v-model="name" class="input-field name-input" placeholder="Acme Ads" />
            <label class="form-label form-label--inline">
                Code *
                <span
                    class="field-tooltip"
                    :class="{ 'field-tooltip--active': codeTooltipVisible }"
                    @click.stop="codeTooltipVisible = !codeTooltipVisible"
                >ⓘ
                    <span v-if="codeTooltipVisible" class="tooltip-bubble">Used in partner link URL: gameartkit.com/?ref={code}</span>
                </span>
            </label>
            <div class="code-field-row">
                <template v-if="codeAutoGenerated">
                    <span class="code-preview">{{ code || '—' }}</span>
                    <button class="btn-edit-code" type="button" @click="enableCodeEdit">✎ Edit</button>
                </template>
                <input
                    v-else
                    ref="codeInput"
                    v-model="code"
                    class="input-field"
                    placeholder="acme-ads"
                />
            </div>
        </div>

        <!-- Row 2: Source type -->
        <div class="form-group">
            <label class="form-label">Source type <span class="optional-label">(optional)</span></label>
            <div class="source-toggle">
                <button
                    type="button"
                    :class="['toggle-btn', { 'toggle-btn--active': source_type === 'influencer' }]"
                    @click="source_type = source_type === 'influencer' ? '' : 'influencer'"
                >Influencer</button>
                <button
                    type="button"
                    :class="['toggle-btn', { 'toggle-btn--active': source_type === 'affiliate' }]"
                    @click="source_type = source_type === 'affiliate' ? '' : 'affiliate'"
                >Affiliate</button>
            </div>
        </div>

        <!-- Row 3: Description -->
        <div class="form-group">
            <label class="form-label">Description <span class="optional-label">(optional)</span></label>
            <textarea v-model="description" class="input-field" rows="2" placeholder="Optional notes about this partner" />
        </div>

        <!-- Actions -->
        <div class="form-actions">
            <Button label="Cancel" severity="secondary" text @click="$emit('cancel')" />
            <Button label="Create Partner" @click="submit" />
        </div>
    </div>
</template>

<style scoped>
.partner-add-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04);
}
.form-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #0f172a;
    margin-bottom: 2px;
}
.form-row--inline {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
}
.form-label--inline {
    white-space: nowrap;
    flex-shrink: 0;
    margin-bottom: 0;
}
.name-input {
    flex: 2;
    min-width: 0;
}
.form-row--inline .code-field-row {
    flex: 1;
    min-width: 0;
}
.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.form-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 2px;
}
.code-field-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.code-preview {
    font-family: monospace;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 6px 10px;
    color: #475569;
    flex: 1;
    font-size: 0.85rem;
    min-height: 34px;
    display: flex;
    align-items: center;
}
.btn-edit-code {
    background: none;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 7px 12px;
    cursor: pointer;
    font-size: 0.85rem;
    color: #64748b;
    white-space: nowrap;
    line-height: 1.4;
    box-sizing: border-box;
}
.btn-edit-code:hover {
    border-color: #94a3b8;
    color: #334155;
}
.field-tooltip {
    position: relative;
    cursor: pointer;
    color: #94a3b8;
    font-size: 0.82rem;
    margin-left: 3px;
    user-select: none;
}
.field-tooltip--active { color: #16a34a; }
.tooltip-bubble {
    position: absolute;
    left: 0;
    top: calc(100% + 4px);
    background: #1e293b;
    color: #f1f5f9;
    font-size: 0.75rem;
    font-weight: normal;
    white-space: nowrap;
    padding: 5px 10px;
    border-radius: 5px;
    z-index: 200;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
}
.form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding-top: 4px;
    border-top: 1px solid #f1f5f9;
}
.link-nudge {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 0.85rem;
    color: #166534;
}
.btn-nudge {
    background: #16a34a;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 12px;
    cursor: pointer;
    font-size: 0.8rem;
    white-space: nowrap;
}
.btn-nudge:hover { background: #15803d; }
.btn-nudge-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: #4ade80;
    font-size: 1rem;
    margin-left: auto;
    padding: 0 4px;
}
.optional-label {
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 400;
    margin-left: 3px;
}
.source-toggle {
    display: flex;
    gap: 0;
}
.toggle-btn {
    flex: 1;
    padding: 7px 14px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    line-height: 1.4;
}
.toggle-btn:first-child {
    border-radius: 6px 0 0 6px;
}
.toggle-btn:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
}
.toggle-btn:hover:not(.toggle-btn--active) {
    background: #f1f5f9;
    border-color: #cbd5e1;
}
.toggle-btn--active {
    background: #eff6ff;
    color: #2563eb;
    border-color: #93c5fd;
    font-weight: 600;
}
.toggle-btn:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
    z-index: 1;
    position: relative;
}
/* Consistent input field styling */
.input-field {
    width: 100%;
    padding: 7px 10px;
    font-size: 0.85rem;
    line-height: 1.4;
    color: #0f172a;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    outline: none;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.15s;
}
.input-field:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.input-field::placeholder {
    color: #94a3b8;
}
textarea.input-field {
    resize: vertical;
    min-height: 58px;
}
.optional-label {
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 400;
    margin-left: 3px;
}
.source-toggle {
    display: flex;
    gap: 0;
}
.toggle-btn {
    flex: 1;
    padding: 7px 14px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    line-height: 1.4;
}
.toggle-btn:first-child {
    border-radius: 6px 0 0 6px;
}
.toggle-btn:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
}
.toggle-btn:hover:not(.toggle-btn--active) {
    background: #f1f5f9;
    border-color: #cbd5e1;
}
.toggle-btn--active {
    background: #eff6ff;
    color: #2563eb;
    border-color: #93c5fd;
    font-weight: 600;
}
.toggle-btn:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
    z-index: 1;
    position: relative;
}
/* Consistent input field styling */
.input-field {
    width: 100%;
    padding: 7px 10px;
    font-size: 0.85rem;
    line-height: 1.4;
    color: #0f172a;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    outline: none;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.15s;
}
.input-field:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.input-field::placeholder {
    color: #94a3b8;
}
textarea.input-field {
    resize: vertical;
    min-height: 58px;
}
</style>
